// Waiting-room panels — guest-facing (inside ConferenceRoomGate, before a
// join token exists) and host-facing (inside ConferenceCallRoom, once in
// the call). Both are thin views over the already-live, already-tested
// waiting-room API (api.commandCenter.meetings.waitingRoom.*) — no new
// tables, no new routes. The concierge (mtg-01) speaks only inside this
// thread; admission is always a human decision.

import { useEffect, useRef, useState } from 'react'
import { api } from '../../../../api/client'
import type { WaitingMessage, WaitingSession } from '../../../../api/client'

const MONO = 'var(--sc-font-mono, "JetBrains Mono", ui-monospace, monospace)'

function senderLabel(m: WaitingMessage): string {
  if (m.sender_type === 'agent') return 'Concierge'
  if (m.sender_type === 'host') return 'Host'
  return 'You'
}

// ---- Guest view: rendered by ConferenceRoomGate while status === 'waiting' ----
export function WaitingRoomGuestView({
  session,
  onAdmitted,
  onTerminal,
}: {
  session: WaitingSession
  onAdmitted: () => void
  onTerminal: (reason: string) => void
}) {
  const [messages, setMessages] = useState<WaitingMessage[]>([])
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  const sessionIdRef = useRef(session.id)
  sessionIdRef.current = session.id

  useEffect(() => {
    let cancelled = false
    const poll = async () => {
      try {
        const res = await api.commandCenter.meetings.waitingRoom.messages(sessionIdRef.current)
        if (cancelled) return
        setMessages(res.messages)
        if (res.session.status === 'admitted') { onAdmitted(); return }
        if (res.session.status === 'left' || res.session.status === 'expired') {
          onTerminal(`waiting_room_${res.session.status}`)
          return
        }
      } catch {
        // Transient poll failure — try again on the next tick rather than
        // terminating the wait on one dropped request.
      }
    }
    void poll()
    const id = window.setInterval(poll, 4000)
    return () => { cancelled = true; window.clearInterval(id) }
  }, [session.id])

  const send = async () => {
    const body = draft.trim()
    if (!body || sending) return
    setSending(true)
    try {
      const res = await api.commandCenter.meetings.waitingRoom.send(session.id, body)
      setMessages(res.messages)
      setDraft('')
    } catch {
      // Honest no-op on failure — the composer stays populated so the
      // guest can retry; no fabricated confirmation.
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="sq-conference-gate" role="status" aria-live="polite">
      <div className="sq-conference-gate__card" style={{ maxWidth: 460, width: '100%' }}>
        <span className="sq-conference-gate__badge">WAITING ROOM</span>
        <p>You're waiting for the host to admit you. The Meeting Concierge can take agenda items and relay timeliness updates — nothing here starts your camera or microphone.</p>
        <div style={{ textAlign: 'left', maxHeight: 220, overflowY: 'auto', margin: '14px 0', display: 'grid', gap: 8, padding: '10px 12px', borderRadius: 10, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
          {messages.length === 0 && <p style={{ margin: 0, fontSize: 11.5, color: '#5c6675' }}>No messages yet.</p>}
          {messages.map((m) => (
            <div key={m.id}>
              <div style={{ fontFamily: MONO, fontSize: 9, fontWeight: 800, letterSpacing: '0.06em', color: m.sender_type === 'agent' ? '#7fe6ff' : m.sender_type === 'host' ? '#ffce7a' : '#8a93a3' }}>{senderLabel(m)}</div>
              <div style={{ fontSize: 12.5, color: '#e8ecf2', lineHeight: 1.5 }}>{m.body}</div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void send() }}
            placeholder="Message the concierge…"
            aria-label="Message the waiting-room concierge"
            style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.14)', background: 'rgba(255,255,255,0.03)', color: '#e8ecf2', fontSize: 12.5 }}
          />
          <button type="button" onClick={() => void send()} disabled={sending || !draft.trim()}>Send</button>
        </div>
      </div>
    </div>
  )
}

// ---- Host view: opened from ConferenceCallRoom's header while in-call ----
export function WaitingRoomHostPanel({ meetingId, onClose }: { meetingId: string; onClose: () => void }) {
  const [sessions, setSessions] = useState<WaitingSession[]>([])
  const [note, setNote] = useState('')
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const res = await api.commandCenter.meetings.waitingRoom.sessions(meetingId)
        if (!cancelled) { setSessions(res.sessions); setError(null) }
      } catch {
        if (!cancelled) setError('Could not load the waiting room — retrying.')
      }
    }
    void load()
    const id = window.setInterval(load, 8000)
    return () => { cancelled = true; window.clearInterval(id) }
  }, [meetingId])

  const admit = async (sessionId: string) => {
    setBusyId(sessionId)
    try {
      await api.commandCenter.meetings.waitingRoom.admit(sessionId)
      setSessions((prev) => prev.map((s) => (s.id === sessionId ? { ...s, status: 'admitted' } : s)))
    } catch {
      setError('Admit failed — the session may have already changed state.')
    } finally {
      setBusyId(null)
    }
  }

  const sendNote = async () => {
    const body = note.trim()
    if (!body) return
    try {
      await api.commandCenter.meetings.waitingRoom.hostUpdate(meetingId, { note: body })
      setNote('')
    } catch {
      setError('Could not relay the note to waiting guests.')
    }
  }

  const waiting = sessions.filter((s) => s.status === 'waiting')

  return (
    <aside className="cr-panel" aria-label="Waiting room" role="dialog" aria-modal="false" style={{ position: 'absolute', top: 56, right: 16, width: 320, maxWidth: 'calc(100vw - 32px)', maxHeight: '70vh', zIndex: 4 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <span style={{ fontFamily: MONO, fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em', color: '#e8ecf2' }}>WAITING ROOM · {waiting.length} waiting</span>
        <button type="button" onClick={onClose} aria-label="Close waiting room" style={{ background: 'none', border: 'none', color: '#8a93a3', cursor: 'pointer' }}>✕</button>
      </div>
      <div style={{ padding: 10, overflowY: 'auto', display: 'grid', gap: 8 }}>
        {error && <p style={{ fontSize: 11, color: '#ff7a7a', margin: 0 }}>{error}</p>}
        {sessions.length === 0 && !error && <p className="cr-state-note">No one is waiting.</p>}
        {sessions.map((s) => (
          <div key={s.id} style={{ padding: '8px 10px', borderRadius: 8, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: '#e8ecf2' }}>{s.display_name || 'Guest'}</span>
              <span style={{ fontFamily: MONO, fontSize: 9, color: '#5c6675', textTransform: 'uppercase' }}>{s.status}</span>
            </div>
            {(s.agenda_items?.length ?? 0) > 0 && (
              <p style={{ margin: '4px 0 0', fontSize: 11, color: '#8a93a3' }}>{s.agenda_items![0]}</p>
            )}
            {s.status === 'waiting' && (
              <button type="button" onClick={() => void admit(s.id)} disabled={busyId === s.id} style={{ marginTop: 6 }}>
                {busyId === s.id ? 'Admitting…' : 'Admit'}
              </button>
            )}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6, padding: '8px 10px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void sendNote() }}
          placeholder="Relay a timeliness note…"
          aria-label="Relay a note to all waiting guests"
          style={{ flex: 1, padding: '6px 8px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.14)', background: 'rgba(255,255,255,0.03)', color: '#e8ecf2', fontSize: 11.5 }}
        />
        <button type="button" onClick={() => void sendNote()} disabled={!note.trim()}>Send</button>
      </div>
    </aside>
  )
}
